/**
 * Branding por negocio.
 *
 * Cada negocio elige tres colores y su página pública (aturno.app/<slug>) se
 * pinta con ellos. La estructura —tipografía, espaciados, radios, componentes—
 * sale del sistema de Aturno; el color sale del negocio.
 *
 * El puente entre las dos cosas está en `aplicarBranding`: además de las
 * variables --brand-* que ya consumía el CSS a mano (59 referencias en
 * BusinessPage.css), escribe los tokens que leen los componentes de shadcn
 * (--primary, --primary-foreground, --ring). Con eso un <Button> toma el color
 * del negocio solo, sin que haya que escribir un color en ningún componente.
 *
 * Estas funciones estaban definidas adentro de BusinessPage.jsx, duplicadas
 * cuatro veces en la misma función y copiadas otra vez en BrandPreview.jsx y
 * Dashboard.jsx.
 */

/**
 * Colores de Aturno, cuando el negocio no eligió ninguno.
 *
 * El primario es #FF5722, el mismo naranja de la landing y de los botones de
 * toda la app. Antes acá decía #D84315, un naranja más oscuro que no se usaba
 * en ningún otro lado: un negocio nuevo arrancaba con una marca que no era la
 * de Aturno. El propio archivo ya se contradecía, porque el fallback de
 * `hexToHsl` más abajo es '14 100% 57%', que es justamente #FF5722.
 *
 * Este es el único lugar donde vive el color de Aturno. Lo que necesite el
 * naranja debería leer `var(--brand-primary)` o el token `--primary` que
 * escribe `aplicarBranding`, no repetir el hex.
 */
export const COLORES_POR_DEFECTO = {
  primaryColor: '#FF5722',
  secondaryColor: '#C2185B',
  accentColor: '#4A148C',
};

/**
 * ¿El texto encima de este color va blanco?
 *
 * Antes esto era `isColorDark` con la fórmula BT.601 y un corte en 128. El
 * naranja de Aturno da 131.19 ahí: quedaba tres puntos del lado "claro", así
 * que todo lo que branding pinta —`--brand-text-on-primary` y el token
 * `--primary-foreground` de shadcn— salía en casi negro sobre naranja. Y sólo
 * en las pantallas donde branding corre (la página pública y el panel), porque
 * `index.css` tiene `--primary-foreground` en blanco: el mismo botón se veía
 * blanco en la landing y negro en el panel.
 *
 * Acá se usa luminancia relativa de WCAG, que es la que define el contraste de
 * verdad, y el corte está alto a propósito: sólo los colores pálidos se llevan
 * texto oscuro. Un negocio que elige amarillo o rosa pastel necesita texto
 * oscuro para poder leerse; uno que elige un tono saturado —naranja, rojo,
 * verde, petróleo— espera blanco, que es lo que hacen Material, Bootstrap y
 * iOS.
 *
 * Vale decirlo derecho: sobre el naranja #FF5722 el negro contrasta mejor que
 * el blanco (6.6:1 contra 3.2:1). El blanco cumple el 3:1 que WCAG pide para
 * componentes de interfaz y texto grande, no el 4.5:1 de texto chico. Es una
 * decisión de marca, tomada a sabiendas: si algún día un botón naranja lleva
 * texto chico, ahí sí hay que mirar el número.
 */
export function necesitaTextoBlanco(hexColor) {
  if (!hexColor || typeof hexColor !== 'string') return true;
  const hex = hexColor.replace('#', '');
  if (hex.length !== 6) return true;

  const canal = (v) => {
    const x = parseInt(v, 16) / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  const luminancia =
    0.2126 * canal(hex.slice(0, 2)) +
    0.7152 * canal(hex.slice(2, 4)) +
    0.0722 * canal(hex.slice(4, 6));

  return luminancia <= 0.45;
}

/** #RRGGBB -> rgba(r, g, b, alpha) */
export function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== 'string') return `rgba(0, 0, 0, ${alpha})`;
  const limpio = hex.replace('#', '');
  if (limpio.length !== 6) return `rgba(0, 0, 0, ${alpha})`;
  const r = parseInt(limpio.slice(0, 2), 16);
  const g = parseInt(limpio.slice(2, 4), 16);
  const b = parseInt(limpio.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * El mismo color, más claro o más oscuro.
 *
 * Hace falta porque la paleta de Aturno tiene un tono base, uno claro y uno
 * oscuro (`--aturno-orange`, `-light`, `-dark`), y el hover de medio panel es
 * "el mismo botón, un tono más oscuro". Si el claro y el oscuro se sacaran del
 * secundario y del acento de la paleta elegida, el color libre —donde los tres
 * tonos son el MISMO hex, ver SelectorDeColores— dejaría todos esos hovers
 * planos: el botón no cambiaría al pasarle el mouse por encima.
 *
 * Mezcla contra blanco o negro en vez de tocar la luminosidad en HSL: es la
 * cuenta que hace `color-mix`, y a diferencia de subir la L no lava el color
 * hasta el gris cuando el original ya es muy claro o muy oscuro.
 */
export function mezclar(hex, hacia, cantidad) {
  const limpio = (hex || '').replace('#', '');
  if (limpio.length !== 6) return hex;
  const objetivo = hacia === 'blanco' ? 255 : 0;
  const canal = (i) => {
    const valor = parseInt(limpio.slice(i, i + 2), 16);
    return Math.round(valor + (objetivo - valor) * cantidad)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${canal(0)}${canal(2)}${canal(4)}`;
}

/**
 * #RRGGBB -> "H S% L%", el formato sin coma que espera tailwind.config.js
 * para envolverlo en hsl(). Es lo que permite que el color del negocio entre
 * en los tokens de shadcn.
 */
export function hexToHsl(hex) {
  const limpio = (hex || '').replace('#', '');
  if (limpio.length !== 6) return '14 100% 57%'; // el naranja de Aturno
  const r = parseInt(limpio.slice(0, 2), 16) / 255;
  const g = parseInt(limpio.slice(2, 4), 16) / 255;
  const b = parseInt(limpio.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Todas las propiedades que escribe aplicarBranding, para poder limpiarlas. */
const PROPIEDADES = [
  '--brand-primary', '--brand-secondary', '--brand-accent',
  '--brand-text-on-primary', '--brand-text-on-secondary', '--brand-text-on-accent',
  '--brand-primary-light', '--brand-primary-lighter', '--brand-primary-shadow',
  '--brand-secondary-light', '--brand-accent-light',
  '--primary-color', '--secondary-color', '--accent-color',
  '--primary', '--primary-foreground', '--ring',
  '--aturno-orange', '--aturno-orange-light', '--aturno-orange-dark',
  '--aturno-violet', '--aturno-violet-light', '--aturno-violet-dark',
];

/**
 * Escribe el branding del negocio como custom properties.
 *
 * Va sobre document.documentElement a propósito, no sobre un contenedor de la
 * página: los diálogos de shadcn se renderizan con un portal de Radix colgando
 * de document.body, así que si se scopeara a la página los modales quedarían
 * sin los colores del negocio. Para que el color no se filtre a otras
 * pantallas al navegar, la página llama a `limpiarBranding` al desmontarse.
 */
export function aplicarBranding({ primaryColor, secondaryColor, accentColor } = {}) {
  const root = document.documentElement;
  const primario = primaryColor || COLORES_POR_DEFECTO.primaryColor;
  const secundario = secondaryColor || COLORES_POR_DEFECTO.secondaryColor;
  const acento = accentColor || COLORES_POR_DEFECTO.accentColor;

  const textoSobre = (color) => (necesitaTextoBlanco(color) ? 'white' : '#333333');

  root.style.setProperty('--brand-primary', primario);
  root.style.setProperty('--brand-secondary', secundario);
  root.style.setProperty('--brand-accent', acento);
  root.style.setProperty('--brand-text-on-primary', textoSobre(primario));
  root.style.setProperty('--brand-text-on-secondary', textoSobre(secundario));
  root.style.setProperty('--brand-text-on-accent', textoSobre(acento));

  root.style.setProperty('--brand-primary-light', hexToRgba(primario, 0.1));
  root.style.setProperty('--brand-primary-lighter', hexToRgba(primario, 0.05));
  root.style.setProperty('--brand-primary-shadow', hexToRgba(primario, 0.3));
  root.style.setProperty('--brand-secondary-light', hexToRgba(secundario, 0.1));
  root.style.setProperty('--brand-accent-light', hexToRgba(acento, 0.1));

  // Alias que ya existían para compatibilidad con CSS viejo
  root.style.setProperty('--primary-color', primario);
  root.style.setProperty('--secondary-color', secundario);
  root.style.setProperty('--accent-color', acento);

  // El puente con shadcn: a partir de acá, Button, Badge, RadioGroup y todo lo
  // que use bg-primary / ring-ring toma el color del negocio automáticamente.
  root.style.setProperty('--primary', hexToHsl(primario));
  root.style.setProperty('--primary-foreground', necesitaTextoBlanco(primario) ? '0 0% 100%' : '0 0% 9%');
  root.style.setProperty('--ring', hexToHsl(primario));

  /**
   * El puente con el CSS escrito a mano: la paleta de Aturno, pisada.
   *
   * `--brand-primary` y los tokens de shadcn de acá arriba sólo alcanzan a lo
   * que fue escrito para leerlos, y casi nada del panel lo fue: entre sus 23
   * hojas de estilo hay ~150 usos de `var(--aturno-orange)` y sus variantes
   * contra 4 de `var(--brand-primary)`. Por eso el negocio elegía su color, lo
   * veía en la vista previa de su página pública, volvía al panel y encontraba
   * todo naranja igual que antes: el calendario, la barra lateral, los botones
   * de guardar, las pestañas. El color no se estaba perdiendo, lo estaba
   * escribiendo en variables que esas hojas no leen.
   *
   * Pisar la paleta acá recolorea las tres cosas de una —el CSS a mano, shadcn y
   * los componentes que ya leían `--brand-*`— sin tocar una sola hoja de estilo,
   * y sin dejar islas naranjas en las pantallas que nadie se acuerde de migrar.
   * Como todo esto se escribe inline sobre <html>, gana sobre el `:root` de
   * index.css; y `limpiarBranding` lo borra al salir, así que la landing, el
   * login y el resto de Aturno siguen siendo naranjas.
   *
   * El violeta —el otro miembro de la paleta, el que cierra los degradados
   * `naranja → violeta`— pasa a ser el secundario del negocio: dejarlo violeta
   * partiría cada degradado entre el color elegido y uno que no tiene nada que
   * ver. Los tonos claro y oscuro se derivan del propio color en vez de salir
   * del acento y el secundario de la paleta, para que el color libre —donde los
   * tres tonos son el mismo hex— no deje los hovers planos.
   */
  root.style.setProperty('--aturno-orange', primario);
  root.style.setProperty('--aturno-orange-light', mezclar(primario, 'blanco', 0.2));
  root.style.setProperty('--aturno-orange-dark', mezclar(primario, 'negro', 0.2));
  root.style.setProperty('--aturno-violet', secundario);
  root.style.setProperty('--aturno-violet-light', mezclar(secundario, 'blanco', 0.2));
  root.style.setProperty('--aturno-violet-dark', mezclar(secundario, 'negro', 0.2));
}

/** Devuelve los tokens a los de Aturno al salir de la página del negocio. */
export function limpiarBranding() {
  const root = document.documentElement;
  PROPIEDADES.forEach((p) => root.style.removeProperty(p));
}
